import { useFormikContext } from 'formik';
import { isNil } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../../../shared/permissions';
import { useCompany } from '../../../../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper, { Panel } from '../../../../../../components/wrappers/CollapsibleWrapper';
import { exportFinancialTabsExcel, getWorkbookToExport } from '../../../../../../utilities/excel';
import hasPermissions from '../../../../../../utilities/hasPermissions';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import { type FinanceProductFormValues } from '../../index';
import EditableTable from '../EditableTable';
import useInsuranceFeeReference from './useInsuranceFeeReference';

const mappedHeaders = {
    'Variant Name': 'variantName',
    'Reference*': 'variantSuiteId',
    'Insurance Fee (%)*': 'value',
};

/**
 * Insurance Fee Table component
 */
const InsuranceFeeTable = ({ disabled = false }: { disabled: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { variantColumns, finderVehicleColumns, variantRecords, finderVehiclesRecords, displayName } =
        useInsuranceFeeReference({ isDisabled: disabled });

    const company = useCompany(true);
    const showFinderAssignment =
        company?.availableModules.hasFinderVehicleManagementModules &&
        hasPermissions(company?.permissions, [permissionKind.viewFinderVehicles]);

    const { setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const onImport = useCallback(
        async (data: Record<string, string>[]) => {
            const updatedTable = data
                .filter(record => !isNil(record.value))
                .map(record => ({
                    variantSuiteId: record.variantSuiteId,
                    value: record.value,
                }));

            setFieldValue('insuranceFee.table', updatedTable);
        },
        [setFieldValue]
    );

    const onExport = useCallback(async () => {
        const workbook = await getWorkbookToExport(mappedHeaders);
        const worksheet = workbook.getWorksheet(1);

        [...variantRecords, ...finderVehiclesRecords].forEach((record, index) => {
            worksheet.addRow(record);
        });

        exportFinancialTabsExcel(workbook, displayName, t('financeProductDetails:tables.ucclLeasing.insuranceFee'));
    }, [variantRecords, finderVehiclesRecords, displayName, t]);

    return (
        <CollapsibleWrapper defaultActiveKey={['insuranceFee']}>
            <Panel key="insuranceFee" header={t('financeProductDetails:tables.ucclLeasing.insuranceFee')}>
                <EditableTable columns={variantColumns} records={variantRecords} unitIsPercentage />
                {showFinderAssignment && (
                    <EditableTable columns={finderVehicleColumns} records={finderVehiclesRecords} unitIsPercentage />
                )}
            </Panel>
            {!disabled && (
                <>
                    <ImportExcel
                        mappedHeaders={mappedHeaders}
                        onImport={onImport}
                        title={t('financeProductDetails:importModal.titles.ucclLeasing.insuranceFee')}
                    />
                    <ExportExcel onClick={onExport} />
                </>
            )}
        </CollapsibleWrapper>
    );
};

export default InsuranceFeeTable;
